import axios from "axios";
import type { DashboardWidget } from "../../types/dashboard/dashboard.types";

const backendUrl =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

// Instance Axios unique
const axiosInstance = axios.create({
  baseURL: `${backendUrl}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Intercepteur token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ===========================
   FETCH WIDGETS
=========================== */
export const fetchDashboardWidgets = async (
  id_dashboard: number
): Promise<DashboardWidget[]> => {
  try {
    const response = await axiosInstance.get(
      `/dashboard/${id_dashboard}/widgets`
    );

    return Array.isArray(response.data?.Dashboards)
      ? response.data.Dashboards
      : [];
  } catch (error) {
    console.error("Erreur fetchDashboardWidgets:", error);
    return [];
  }
};

/* ===========================
   ADD WIDGET
=========================== */
export const addWidget = async (
  widget: Omit<DashboardWidget, "id">
): Promise<{ id_widget: number }> => {
  try {
    const response = await axiosInstance.post("/dashboard/add", widget);
    return response.data;
  } catch (error) {
    console.error("Erreur addWidget:", error);
    throw error;
  }
};

/* ===========================
   UPDATE DASHBOARD
=========================== */
export const updateDashboard = async (
  widgets: Array<{
    id_widget: number;
    content: string;
    type: string;
    x: number;
    y: number;
    w: number;
    h: number;
  }>
): Promise<{ message: string }> => {
  const response = await axiosInstance.put("/dashboard/update", { widgets });
  return response.data;
};

/* ===========================
   DELETE WIDGET
=========================== */
export const deleteWidget = async (
  id_widget: string | number
): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.delete(
      `/dashboard/delete/${String(id_widget)}`
    );

    return response.data;
  } catch (error) {
    console.error("Erreur deleteWidget:", error);
    throw error;
  }
};

/* ===========================
   DASHBOARD ENRICHI TYPES
=========================== */
export interface DashboardKpi {
  label: string;
  value: string | number;
  icon: string;
}

export interface DashboardRfqActivity {
  day: string;
  rfq: number;
}

export interface DashboardReadinessItem {
  name: string;
  value: number;
}

export interface DashboardIndustrialCapacity {
  name: string;
  value: number;
  count?: number;
  capacity?: number;
  color: string;
}

export interface DashboardAiMatching {
  rfq: string;
  suppliers: number;
  matchScore: number;
  quality: string;
  bestCandidate: string;
  note: string;
  warning: string;
}

export interface DashboardActivity {
  name: string;
  type: string;
  date: string;
}

export interface DashboardAlert {
  message: string;
  type: "high" | "medium" | "low" | string;
  date: string;
}

export interface DashboardCoverageItem {
  module: string;
  count: number;
  detail: string;
}

export interface DashboardSimpleStat {
  name: string;
  value: number;
  color?: string;
}

export interface DashboardCertificationListItem {
  id: number;
  name: string;
  supplier: string;
  certificateNumber: string;
  issuedDate: string;
  expiryDate: string;
  status: string;
  linkedProduct: string;
  exportImpact: string;
}

export interface DashboardTopProduct {
  idProduct: number;
  name: string;
  supplier: string;
  category: string;
  annualCapacity: number;
  unit: string;
  imagesCount: number;
}

export interface DashboardSupplierProfile {
  idSupplier: number;
  name: string;
  wilaya: string;
  address: string;
  contact: string;
  sector: string;
  products: number;
  machines: number;
  certifications: number;
  documents: number;
  capacity: number;
  employees: number;
  material: string;
  maturity: string;
  supportNeed: string;
  status: string;
  score: number;
}

export interface DashboardSupplierReportItem {
  idSupplier: number;
  name: string;
  legalName: string;
  tradeName: string;
  email: string;
  phone: string;
  wilaya: string;
  country: string;
  address: string;
  website: string;
  supplierType: string;
  companyType: string;
  industrialSector: string;
  aidaCategory: string;
  aidaStatus: string;
  industrialStatus: string;
  employees: number;
  products: number;
  machines: number;
  certifications: number;
  documents: number;
  capacity: number;
}

export interface DashboardFiliereStat {
  name: string;
  suppliers: number;
  products: number;
  capacity: number;
  poles: string[];
}

export interface DashboardStats {
  kpis: DashboardKpi[];
  secondaryKpis: DashboardKpi[];
  rfqActivity: DashboardRfqActivity[];
  rfqActivityRange?: {
    startDate: string;
    endDate: string;
  };
  rfqActivityBounds?: {
    startDate: string;
    endDate: string;
    totalRfqs: number;
  };
  readiness: DashboardReadinessItem[];
  industrialCapacity: DashboardIndustrialCapacity[];
  aiMatching: DashboardAiMatching;
  databaseCoverage: DashboardCoverageItem[];
  rfqPipeline: DashboardSimpleStat[];
  topWilayas: DashboardSimpleStat[];
  topOemWilayas?: DashboardSimpleStat[];
  supplierSegments: DashboardSimpleStat[];
  documentAiStatus: DashboardSimpleStat[];
  certificationStatus: DashboardSimpleStat[];
  certificationList?: DashboardCertificationListItem[];
  topProducts: DashboardTopProduct[];
  supplierProfiles: DashboardSupplierProfile[];
  allSuppliers?: DashboardSupplierReportItem[];
  filiereStats?: DashboardFiliereStat[];
  activities: DashboardActivity[];
  alerts: DashboardAlert[];
}

export interface DashboardStatsParams {
  rfqStartDate?: string;
  rfqEndDate?: string;
}

export const fetchDashboardStats = async (
  params?: DashboardStatsParams
): Promise<DashboardStats | null> => {
  try {
    const response = await axiosInstance.get("/dashboard/stats", { params });
    return response.data;
  } catch (error) {
    console.error("Erreur fetchDashboardStats:", error);
    return null;
  }
};

export default axiosInstance;
